import { requestApi } from "../api/client.js";

// Phones show at most three one-tap answers, so the dashboard keeps three.
export const ANSWERS_SHOWN = 3;

export interface RuleAnswers {
  answers: string[];
  custom: boolean;
  default_answers: string[];
  record_type_code: string;
  rule_code: string;
  title: string;
}

export async function listRuleAnswers(): Promise<RuleAnswers[]> {
  return (await requestApi<{ items: RuleAnswers[] }>("/rule-answers")).items;
}

export function saveRuleAnswers(
  ruleCode: string,
  answers: readonly string[],
): Promise<RuleAnswers> {
  return requestApi<RuleAnswers>(`/rule-answers/${ruleCode}`, {
    body: JSON.stringify({ answers }),
    method: "PUT",
  });
}

export function resetRuleAnswers(ruleCode: string): Promise<RuleAnswers> {
  return requestApi<RuleAnswers>(`/rule-answers/${ruleCode}`, {
    method: "DELETE",
  });
}

// What went wrong, in kitchen words. The rule titles stay the regulatory
// wording; these only name the card.
const readingLabels: Record<string, string> = {
  aw_limit: "Water activity too high",
  calibration_accuracy: "Thermometer reads wrong",
  chill_max: "Fridge too warm",
  cook_poultry_core: "Cooking not hot enough",
  cooling_stages_general: "Cooling too slow",
  cooling_stages_sushi: "Sushi rice cooling too slow",
  delivery_chilled_max: "Chilled delivery too warm",
  duck_drying_max: "Duck drying area too warm",
  freezer_frozen: "Freezer not frozen hard",
  hot_hold_min: "Hot food too cool",
  pickling_ph: "Pickle pH too high",
  reheat_core: "Reheating not hot enough",
  smoke_cook_core: "Hot smoking not hot enough",
  sous_vide_hold: "Sous vide too short or too cool",
  sushi_rice_ph: "Sushi rice pH too high",
  transport_control: "Too long out of temperature in transport",
};

const everyday = new Set([
  "chill_max",
  "freezer_frozen",
  "hot_hold_min",
  "delivery_chilled_max",
  "cook_poultry_core",
  "reheat_core",
  "cooling_stages_general",
]);

export function readingLabel(rule: Pick<RuleAnswers, "rule_code" | "title">) {
  return readingLabels[rule.rule_code] ?? rule.title;
}

// Everyday readings first, in kitchen order; the specialist ones after.
export function splitReadings(rules: readonly RuleAnswers[]): {
  everyday: RuleAnswers[];
  specialist: RuleAnswers[];
} {
  const order = [...everyday];
  return {
    everyday: rules
      .filter((rule) => everyday.has(rule.rule_code))
      .sort(
        (left, right) =>
          order.indexOf(left.rule_code) - order.indexOf(right.rule_code),
      ),
    specialist: rules.filter((rule) => !everyday.has(rule.rule_code)),
  };
}

// Trimmed, without blanks or repeats: what the server will keep.
export function cleanAnswers(answers: readonly string[]): string[] {
  const seen = new Set<string>();
  const kept: string[] = [];
  for (const answer of answers) {
    const text = answer.trim();
    const key = text.toLowerCase();
    if (!text || seen.has(key)) continue;
    seen.add(key);
    kept.push(text);
  }
  return kept.slice(0, ANSWERS_SHOWN);
}

export function moveAnswer(
  answers: readonly string[],
  from: number,
  to: number,
): string[] {
  if (to < 0 || to >= answers.length) return [...answers];
  const next = [...answers];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}
